"use client";

import { useEffect, useRef } from "react";

// WebGL atom for the /photon hero, ported from the design handoff prototype
// (design_handoff_photon_landing/atom/main.js). The scene is tuned for the
// site's dark canvas (#0A0B0D) - the hero band stays dark in both themes.
//
// Performance notes carried over from the prototype:
// - no postprocessing chain; all glow is sprite/point based
// - nucleus is 2 InstancedMeshes + 1 Points layer (3 draw calls)
// - adaptive pixel ratio drops under load, pauses offscreen/hidden

const CANVAS_BG = 0x0a0b0d;

export function PhotonAtom() {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    // three.js only loads on the client, split out of the page bundle.
    import("three").then((THREE) => {
      if (cancelled || !hostRef.current) return;

      const canvas = document.createElement("canvas");
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      canvas.style.display = "block";
      // Hidden until the first rendered frame: an opaque (alpha: false)
      // canvas can composite as white before its first clear, which reads
      // as a flash on refresh while the scene builds.
      canvas.style.opacity = "0";
      canvas.style.transition = "opacity 240ms ease";
      host.appendChild(canvas);

      let renderer: import("three").WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({
          canvas,
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
        });
      } catch {
        // No WebGL: leave the hero without the ornament.
        host.removeChild(canvas);
        return;
      }

      const DPR_CAP = 2;
      let dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      renderer.setPixelRatio(dpr);
      renderer.setClearColor(CANVAS_BG, 1);
      // Paint the dark background immediately, before the (heavy) scene
      // construction below runs.
      renderer.setSize(
        Math.max(1, host.clientWidth),
        Math.max(1, host.clientHeight),
        false,
      );
      renderer.clear();

      const scene = new THREE.Scene();
      scene.fog = new THREE.FogExp2(CANVAS_BG, 0.011);

      const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 220);
      camera.position.set(0, 0, 24);

      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const SPEED = reduced ? 0.15 : 1;

      /* ---------- shared textures (generated once) ---------- */

      function radialTexture(stops: [number, string][], size = 128) {
        const c = document.createElement("canvas");
        c.width = c.height = size;
        const ctx = c.getContext("2d")!;
        const g = ctx.createRadialGradient(
          size / 2,
          size / 2,
          0,
          size / 2,
          size / 2,
          size / 2,
        );
        for (const [at, col] of stops) g.addColorStop(at, col);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, size, size);
        const tex = new THREE.CanvasTexture(c);
        tex.colorSpace = THREE.SRGBColorSpace;
        return tex;
      }

      const glowTex = radialTexture([
        [0, "rgba(255,255,255,1)"],
        [0.25, "rgba(255,255,255,0.55)"],
        [0.6, "rgba(255,255,255,0.12)"],
        [1, "rgba(255,255,255,0)"],
      ]);

      const hazeTex = radialTexture(
        [
          [0, "rgba(255,255,255,0.85)"],
          [0.4, "rgba(255,255,255,0.28)"],
          [1, "rgba(255,255,255,0)"],
        ],
        256,
      );

      /* ---------- lights: atmospheric, pulsing, slowly orbiting ---------- */

      scene.add(new THREE.AmbientLight(0x2a2f45, 0.9));

      type PulseLight = {
        l: import("three").PointLight;
        base: number;
        pos: import("three").Vector3;
        pulseFreq: number;
        pulsePhase: number;
        orbitSpeed: number;
      };
      const lights: PulseLight[] = [];
      function addPulseLight(
        color: number,
        base: number,
        pos: import("three").Vector3,
        pulseFreq: number,
        pulsePhase: number,
        orbitSpeed: number,
      ) {
        const l = new THREE.PointLight(color, base, 0, 2);
        l.position.copy(pos);
        scene.add(l);
        lights.push({ l, base, pos: pos.clone(), pulseFreq, pulsePhase, orbitSpeed });
      }
      addPulseLight(0xf2fbff, 300, new THREE.Vector3(11, 6, 12), 0.9, 0.0, 0.11);
      addPulseLight(0x37e2ff, 210, new THREE.Vector3(-13, -5, 9), 0.6, 2.1, -0.08);
      addPulseLight(0x4f7fff, 200, new THREE.Vector3(0, 12, -12), 0.45, 4.2, 0.06);

      const dirLight = new THREE.DirectionalLight(0xffffff, 0.55);
      dirLight.position.set(4, 8, 10);
      scene.add(dirLight);

      /* ---------- atom root ---------- */

      const atom = new THREE.Group();
      atom.scale.setScalar(0.75);
      scene.add(atom);

      // nucleus spins on its own axis, independent of the orbit shells
      const nucleusGroup = new THREE.Group();
      atom.add(nucleusGroup);

      /* ---------- nucleus: tightly packed animated orbs ---------- */

      const NUCLEUS_R = 1.55;
      const COUNT = 28; // 14 protons + 14 neutrons
      const ORB_R = 0.39;

      // fibonacci lattice inside a sphere, then repulsion passes to pack tight
      const nucBase: import("three").Vector3[] = [];
      for (let i = 0; i < COUNT; i++) {
        const th = Math.PI * (1 + Math.sqrt(5)) * i;
        const rr = NUCLEUS_R * Math.cbrt((i + 0.5) / COUNT);
        const y = 1 - (i / (COUNT - 1)) * 2;
        const rad = Math.sqrt(Math.max(0, 1 - y * y));
        nucBase.push(
          new THREE.Vector3(
            Math.cos(th) * rad * rr + (Math.random() - 0.5) * 0.3,
            y * rr + (Math.random() - 0.5) * 0.3,
            Math.sin(th) * rad * rr + (Math.random() - 0.5) * 0.3,
          ),
        );
      }
      for (let pass = 0; pass < 24; pass++) {
        for (let i = 0; i < COUNT; i++) {
          for (let j = i + 1; j < COUNT; j++) {
            const a = nucBase[i],
              b = nucBase[j];
            const dx = a.x - b.x,
              dy = a.y - b.y,
              dz = a.z - b.z;
            const d2 = dx * dx + dy * dy + dz * dz;
            const min = ORB_R * 1.9;
            if (d2 < min * min && d2 > 1e-6) {
              const d = Math.sqrt(d2);
              const push = ((min - d) * 0.5) / d;
              a.x += dx * push;
              a.y += dy * push;
              a.z += dz * push;
              b.x -= dx * push;
              b.y -= dy * push;
              b.z -= dz * push;
            }
          }
        }
        for (const p of nucBase) {
          const len = p.length();
          const maxR = NUCLEUS_R - ORB_R * 0.55;
          if (len > maxR) p.multiplyScalar(maxR / len);
        }
      }

      const nucPhase = new Float32Array(COUNT);
      const nucFreq = new Float32Array(COUNT);
      const nucPos = new Float32Array(COUNT * 3);
      for (let i = 0; i < COUNT; i++) {
        nucPhase[i] = Math.random() * Math.PI * 2;
        nucFreq[i] = 1.4 + Math.random() * 1.6;
      }

      const orbGeo = new THREE.SphereGeometry(ORB_R, 28, 20);

      // small generated environment so the dark glossy orbs have reflections
      {
        const pmrem = new THREE.PMREMGenerator(renderer);
        const envScene = new THREE.Scene();
        const envGeo = new THREE.SphereGeometry(50, 32, 16);
        const envCol = new Float32Array(envGeo.attributes.position.count * 3);
        const cTop = new THREE.Color(0xc08a52); // warm amber zenith
        const cMid = new THREE.Color(0x1e4a5c); // deep teal horizon
        const cBot = new THREE.Color(0x060a10); // near black floor
        const cc = new THREE.Color();
        for (let i = 0; i < envGeo.attributes.position.count; i++) {
          const y = envGeo.attributes.position.getY(i) / 50;
          if (y > 0) cc.lerpColors(cMid, cTop, Math.pow(y, 1.4));
          else cc.lerpColors(cMid, cBot, Math.pow(-y, 0.8));
          envCol[i * 3] = cc.r;
          envCol[i * 3 + 1] = cc.g;
          envCol[i * 3 + 2] = cc.b;
        }
        envGeo.setAttribute("color", new THREE.BufferAttribute(envCol, 3));
        envScene.add(
          new THREE.Mesh(
            envGeo,
            new THREE.MeshBasicMaterial({
              vertexColors: true,
              side: THREE.BackSide,
            }),
          ),
        );
        // bright "softbox" strips to paint sharp highlights on the orbs
        const boxes: [number, number, number, number, number, number][] = [
          [0xfff0dd, 34, 13, 26, 18, 8],
          [0x9fdcff, 26, 9, -24, 4, 18],
          [0xb49aff, 18, 7, 6, -18, -24],
        ];
        for (const [col, w, h, x, y, z] of boxes) {
          const p = new THREE.Mesh(
            new THREE.PlaneGeometry(w, h),
            new THREE.MeshBasicMaterial({ color: col, side: THREE.DoubleSide }),
          );
          p.position.set(x, y, z);
          p.lookAt(0, 0, 0);
          envScene.add(p);
        }
        scene.environment = pmrem.fromScene(envScene, 0.04, 0.1, 100).texture;
        pmrem.dispose();
      }

      const protonMesh = new THREE.InstancedMesh(
        orbGeo,
        new THREE.MeshStandardMaterial({
          color: 0x4a4239,
          roughness: 0.24,
          metalness: 0.62,
          emissive: 0x0d0b09,
          emissiveIntensity: 0.35,
          envMapIntensity: 2.0,
        }),
        COUNT / 2,
      );
      const neutronMesh = new THREE.InstancedMesh(
        orbGeo,
        new THREE.MeshStandardMaterial({
          color: 0x3d434e,
          roughness: 0.28,
          metalness: 0.62,
          emissive: 0x0b0e13,
          emissiveIntensity: 0.35,
          envMapIntensity: 2.0,
        }),
        COUNT / 2,
      );
      nucleusGroup.add(protonMesh, neutronMesh);

      // soft per-nucleon halo in ONE draw call, shares the same position buffer
      const nucGlowGeo = new THREE.BufferGeometry();
      nucGlowGeo.setAttribute("position", new THREE.BufferAttribute(nucPos, 3));
      const nucGlowColors = new Float32Array(COUNT * 3);
      const tmpC = new THREE.Color();
      for (let i = 0; i < COUNT; i++) {
        tmpC.set(i % 2 === 0 ? 0xb0a698 : 0x8e99ac).multiplyScalar(0.4);
        nucGlowColors[i * 3] = tmpC.r;
        nucGlowColors[i * 3 + 1] = tmpC.g;
        nucGlowColors[i * 3 + 2] = tmpC.b;
      }
      nucGlowGeo.setAttribute("color", new THREE.BufferAttribute(nucGlowColors, 3));
      const nucGlow = new THREE.Points(
        nucGlowGeo,
        new THREE.PointsMaterial({
          map: glowTex,
          size: 1.05,
          vertexColors: true,
          transparent: true,
          opacity: 0.4,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          sizeAttenuation: true,
        }),
      );
      nucleusGroup.add(nucGlow);

      // core haze behind the nucleus
      const coreHazeMat = new THREE.SpriteMaterial({
        map: hazeTex,
        color: 0x9a9088,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const coreHaze = new THREE.Sprite(coreHazeMat);
      coreHaze.scale.setScalar(6);
      nucleusGroup.add(coreHaze);

      /* ---------- electrons: coherent-light orbits ---------- */

      // coherent palette: one icy cyan-white family, tiny tint spread
      const ELECTRON_COLORS = [0x9ceaff, 0x66d5ff, 0x4fc3ff, 0x8fd8ff];
      const FRINGE_COLOR = 0xffb37a; // spectral dispersion accent

      // closed elliptical tube: constant apparent thickness from any angle
      class RopeCurve extends THREE.Curve<import("three").Vector3> {
        constructor(
          private a: number,
          private b: number,
        ) {
          super();
        }
        getPoint(t: number, target = new THREE.Vector3()) {
          return target.set(
            this.a * Math.cos(t * Math.PI * 2),
            this.b * Math.sin(t * Math.PI * 2),
            0,
          );
        }
      }
      function makeRopeGeometry(
        a: number,
        b: number,
        radius: number,
        segs: number,
      ) {
        return new THREE.TubeGeometry(new RopeCurve(a, b), segs, radius, 8, true);
      }

      type Electron = {
        a: number;
        b: number;
        plane: import("three").Group;
        precession: import("three").Group;
        head: import("three").Group;
        haloMat: import("three").SpriteMaterial;
        fringeMat: import("three").SpriteMaterial;
        ring: import("three").Mesh<
          import("three").TubeGeometry,
          import("three").MeshBasicMaterial
        >;
        angle: number;
        speed: number;
        precessionSpeed: number;
        precessionAxis: import("three").Vector3;
      };
      const electrons: Electron[] = [];

      for (let i = 0; i < ELECTRON_COLORS.length; i++) {
        const color = ELECTRON_COLORS[i];
        const a = 2.9 + i * 0.55; // semi-major axis
        const b = a * (0.62 + 0.1 * Math.sin(i * 2.3)); // eccentric flattening

        // orbit plane with its own tilt, nested so it can precess
        const precession = new THREE.Group();
        const plane = new THREE.Group();
        plane.rotation.set(0.5 + i * 0.62, i * 1.26, 0.35 * i);
        precession.add(plane);
        atom.add(precession);

        const ringGeo = makeRopeGeometry(a, b, 0.1, 200);
        const ring = new THREE.Mesh(
          ringGeo,
          new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.34,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          }),
        );
        plane.add(ring);

        // electron: pure probability-cloud glow, no solid core
        const head = new THREE.Group();
        const haloMat = new THREE.SpriteMaterial({
          map: glowTex,
          color,
          transparent: true,
          opacity: 0.95,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        const halo = new THREE.Sprite(haloMat);
        halo.scale.setScalar(2.4);
        head.add(halo);

        // spectral fringe: faint warm halo, slightly larger, counter-flickers
        const fringeMat = new THREE.SpriteMaterial({
          map: glowTex,
          color: FRINGE_COLOR,
          transparent: true,
          opacity: 0.18,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        const fringe = new THREE.Sprite(fringeMat);
        fringe.scale.setScalar(3.6);
        head.add(fringe);
        plane.add(head);

        electrons.push({
          a,
          b,
          plane,
          precession,
          head,
          haloMat,
          fringeMat,
          ring,
          angle: (i / ELECTRON_COLORS.length) * Math.PI * 2,
          speed: (1.05 / Math.sqrt(a)) * (i % 2 === 0 ? 1 : -1) * 2.2,
          precessionSpeed: (0.14 + i * 0.05) * (i % 2 === 0 ? 1 : -1),
          precessionAxis: new THREE.Vector3(
            Math.sin(1.3 + i * 2.1),
            1,
            Math.cos(0.7 + i * 1.9),
          ).normalize(),
        });
      }

      /* ---------- nucleus glow cloud: faint blueish/orangish haze ---------- */

      const cloudGroup = new THREE.Group();
      atom.add(cloudGroup);
      type CloudPuff = {
        sp: import("three").Sprite;
        base: import("three").Vector3;
        scale: number;
        phase: number;
        freq: number;
        orbit: number;
        baseOp: number;
      };
      const cloudPuffs: CloudPuff[] = [];
      const CLOUD_COLORS = [
        0x3a6cff, 0x2f9dff, 0x64b5ff, 0x2e8fff, 0x7cc4ff, 0xbfe6ff, // icy blues
        0xffb37a, 0xff9a5c, // sparse warm fringe
      ];
      for (let i = 0; i < 18; i++) {
        const warm = i % 6 === 5;
        const m = new THREE.SpriteMaterial({
          map: hazeTex,
          color: warm ? CLOUD_COLORS[6 + (i % 2)] : CLOUD_COLORS[i % 6],
          transparent: true,
          opacity: 0.15,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        const sp = new THREE.Sprite(m);
        const r = 0.8 + Math.random() * 2.0;
        const th = Math.random() * Math.PI * 2;
        const ph = Math.acos(2 * Math.random() - 1);
        const base = new THREE.Vector3(
          r * Math.sin(ph) * Math.cos(th),
          r * Math.sin(ph) * Math.sin(th),
          r * Math.cos(ph),
        );
        sp.position.copy(base);
        const scale = 2.8 + Math.random() * 2.6;
        sp.scale.setScalar(scale);
        cloudGroup.add(sp);
        cloudPuffs.push({
          sp,
          base,
          scale,
          phase: Math.random() * Math.PI * 2,
          freq: 0.8 + Math.random() * 1.3,
          orbit: (warm ? -1 : 1) * (0.1 + Math.random() * 0.14),
          baseOp: 0.32 + Math.random() * 0.2,
        });
      }

      /* ---------- pause when hidden / offscreen ---------- */

      let running = !document.hidden;
      let inView = true;
      const onVisibility = () => {
        running = !document.hidden;
      };
      document.addEventListener("visibilitychange", onVisibility);
      const io = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
      });
      io.observe(canvas);

      /* ---------- size to the (square) host element ---------- */

      const resize = () => {
        const w = Math.max(1, host.clientWidth);
        const h = Math.max(1, host.clientHeight);
        camera.aspect = w / h;
        // Hold the horizontal field of view constant (42° - the original
        // square framing) so the atom's size tracks the canvas width. A
        // shorter, wide canvas (the mobile hero) then crops empty vertical
        // space instead of shrinking the atom.
        camera.fov =
          (Math.atan(Math.tan((42 * Math.PI) / 360) / camera.aspect) * 360) /
          Math.PI;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h, false);
      };
      resize();
      const ro = new ResizeObserver(resize);
      ro.observe(host);

      /* ---------- per-frame update ---------- */

      const dummy = new THREE.Object3D();
      const _q = new THREE.Quaternion();
      const _n = new THREE.Vector3();
      const _v = new THREE.Vector3();
      const clock = new THREE.Clock();
      let t = 0;

      // adaptive quality: EMA of frame time, step DPR between 1 and the cap
      let frameEMA = 16;
      let framesSinceAdjust = 0;

      function updateNucleus() {
        let p = 0,
          n = 0;
        for (let i = 0; i < COUNT; i++) {
          const base = nucBase[i];
          const ph = nucPhase[i],
            fq = nucFreq[i];
          // breathing jostle: tiny orbital wobble per nucleon
          const wx = Math.sin(t * fq + ph) * 0.04;
          const wy = Math.cos(t * fq * 0.9 + ph * 1.7) * 0.04;
          const wz = Math.sin(t * fq * 1.1 + ph * 0.6) * 0.04;
          const x = base.x + wx,
            y = base.y + wy,
            z = base.z + wz;
          nucPos[i * 3] = x;
          nucPos[i * 3 + 1] = y;
          nucPos[i * 3 + 2] = z;

          const s = 1 + 0.05 * Math.sin(t * fq * 1.4 + ph);
          dummy.position.set(x, y, z);
          dummy.scale.setScalar(s);
          dummy.updateMatrix();
          if (i % 2 === 0) protonMesh.setMatrixAt(p++, dummy.matrix);
          else neutronMesh.setMatrixAt(n++, dummy.matrix);
        }
        protonMesh.instanceMatrix.needsUpdate = true;
        neutronMesh.instanceMatrix.needsUpdate = true;
        nucGlowGeo.attributes.position.needsUpdate = true;
        coreHazeMat.opacity = 0.2 + 0.05 * Math.sin(t * 1.7);
      }

      function updateElectrons(dt: number) {
        for (const e of electrons) {
          e.angle += e.speed * dt * SPEED;
          e.precession.rotateOnAxis(e.precessionAxis, e.precessionSpeed * dt * SPEED);

          e.haloMat.opacity = 0.8 + 0.2 * Math.sin(t * 3 + e.angle);
          e.fringeMat.opacity = 0.15 + 0.06 * Math.sin(t * 2.2 + e.angle);
          e.head.position.set(Math.cos(e.angle) * e.a, Math.sin(e.angle) * e.b, 0);

          // keep a floor on the edge-on fade so rings never fully vanish
          _q.setFromRotationMatrix(e.plane.matrixWorld);
          _n.set(0, 0, 1).applyQuaternion(_q);
          _v.copy(camera.position).normalize();
          const facing = Math.abs(_n.dot(_v));
          const fade = 0.55 + 0.45 * Math.min(1, facing / 0.35);
          e.ring.material.opacity = (0.26 + 0.07 * Math.sin(t * 1.3 + e.a)) * fade;
        }
      }

      function updateAtmosphere(dt: number) {
        for (const o of lights) {
          o.l.intensity = o.base * (0.72 + 0.28 * Math.sin(t * o.pulseFreq + o.pulsePhase));
          const ang = t * o.orbitSpeed;
          const c = Math.cos(ang),
            s = Math.sin(ang);
          o.l.position.set(
            o.pos.x * c - o.pos.z * s,
            o.pos.y + Math.sin(t * 0.4 + o.pulsePhase) * 1.5,
            o.pos.x * s + o.pos.z * c,
          );
        }
        // smoldering cloud: layered sine flicker, never fully calm
        cloudGroup.rotation.y += dt * 0.12 * SPEED;
        for (const p of cloudPuffs) {
          const s1 = Math.sin(t * p.freq + p.phase);
          const s2 = Math.sin(t * p.freq * 2.7 + p.phase * 2.3);
          const flick = Math.max(0, 0.5 + 0.32 * s1 + 0.22 * s2);
          p.sp.material.opacity = p.baseOp * flick;
          const ang = t * p.orbit;
          const c = Math.cos(ang),
            si = Math.sin(ang);
          p.sp.position.set(
            p.base.x * c - p.base.z * si,
            p.base.y + Math.sin(t * 0.35 + p.phase) * 0.3,
            p.base.x * si + p.base.z * c,
          );
          p.sp.scale.setScalar(p.scale * (0.85 + 0.3 * flick));
        }
      }

      function updateCamera() {
        // pure ambient drift, no interaction
        camera.position.x = Math.sin(t * 0.1) * 1.1;
        camera.position.y = Math.cos(t * 0.13) * 0.8;
        camera.lookAt(0, 0, 0);
      }

      function adaptQuality(dtMs: number) {
        frameEMA += (dtMs - frameEMA) * 0.05;
        if (++framesSinceAdjust < 120) return;
        framesSinceAdjust = 0;
        if (frameEMA > 20 && dpr > 1) {
          dpr = Math.max(1, dpr - 0.25);
          renderer.setPixelRatio(dpr);
        } else if (
          frameEMA < 12 &&
          dpr < Math.min(window.devicePixelRatio || 1, DPR_CAP)
        ) {
          dpr = Math.min(DPR_CAP, dpr + 0.25);
          renderer.setPixelRatio(dpr);
        }
      }

      let raf = 0;
      let revealed = false;
      function tick() {
        raf = requestAnimationFrame(tick);
        const dt = Math.min(clock.getDelta(), 0.05);
        if (!running || !inView) return;
        t += dt * SPEED;

        // nucleus self-rotation with a slow axial wobble
        nucleusGroup.rotation.y += dt * 0.55 * SPEED;
        nucleusGroup.rotation.x = Math.sin(t * 0.3) * 0.25;

        updateNucleus();
        updateElectrons(dt);
        updateAtmosphere(dt);
        updateCamera();
        adaptQuality(dt * 1000);

        renderer.render(scene, camera);
        if (!revealed) {
          revealed = true;
          canvas.style.opacity = "1";
        }
      }
      tick();

      cleanup = () => {
        cancelAnimationFrame(raf);
        document.removeEventListener("visibilitychange", onVisibility);
        io.disconnect();
        ro.disconnect();
        scene.traverse((obj) => {
          const mesh = obj as Partial<import("three").Mesh>;
          if (mesh.geometry) mesh.geometry.dispose();
          const material = (mesh as { material?: unknown }).material;
          for (const mat of Array.isArray(material) ? material : [material]) {
            if (mat) (mat as import("three").Material).dispose();
          }
        });
        scene.environment?.dispose();
        glowTex.dispose();
        hazeTex.dispose();
        renderer.dispose();
        canvas.remove();
      };
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  return <div ref={hostRef} className="photonm-atom" aria-hidden="true" />;
}
